import { io, Socket } from 'socket.io-client';
import type { PaymentPageTrackAction } from '../types/paymentPageEvents';
import { PAYMENT_PAGE_REQ_TYPE_TRACK_ACTION } from '../types/paymentPageEvents';
import FingerprintJS, { type GetResult } from '@fingerprintjs/fingerprintjs';

/**
 * Payment-page RPC actions sent via `payment_page_event` (Socket.IO ack).
 * Analytics tracking uses req_type TRACK_ACTION — see paymentPageEvents.ts.
 *
 * VALUE_EXISTS is intentionally not a socket RPC: core-v1 PaymentPageReqType
 * does not include it; use HTTP `/transactions/value-exists` instead.
 */
export type PaymentPageRpcAction = 'START_TRANSACTION';

export type PaymentPageAck =
  | { ok: true; exists?: boolean; data?: unknown; [key: string]: unknown }
  | { ok: false; error?: string; message?: string; [key: string]: unknown };

/**
 * URL of the payment page socket server (core-v1 socket-service, Socket.IO v4).
 * Must be set at build time. Example: http://localhost:5555 for local dev.
 */
const SOCKET_URL = (process.env.REACT_APP_PAYMENT_SOCKET_URL || '').replace(/\/+$/, '');

const DEVICE_UUID_STORAGE_KEY = 'payment_page_device_uuid';
const FINGERPRINT_STORAGE_KEY = 'payment_page_fingerprint';
const DEFAULT_RPC_TIMEOUT_MS = 30000;

/** Browser/device snapshot + full FingerprintJS result (components, confidence, version). */
export type PaymentPageFingerprint = {
  language: string;
  languages: string[];
  timezone: string;
  platform: string;
  user_agent: string;
  screen: {
    width: number;
    height: number;
    avail_width: number;
    avail_height: number;
  };
  color_depth: number;
  pixel_ratio: number;
  hardware_concurrency?: number;
  device_memory?: number;
  touch_support: boolean;
  cookie_enabled: boolean;
  online: boolean;
  /** Full output of FingerprintJS `agent.get()` (JSON-safe). */
  fingerprintjs?: {
    visitor_id: string;
    confidence: GetResult['confidence'];
    version: string;
    components: Record<string, unknown>;
  };
};

/** Event name emitted by core-v1 for payment-page status (on connect + on status change). */
export const TRANSACTION_STATUS_EVENT = 'transaction_status';

export const PAYMENT_PAGE_REQ_TYPE_START_TRANSACTION = 'START_TRANSACTION' as const;

let socket: Socket | null = null;
let currentHashedId: string | null = null;
let currentBuildVersion: number | null = null;
let pageLoadedSent = false;
let connectPromise: Promise<Socket> | null = null;
let fingerprintInitPromise: Promise<void> | null = null;

export function isPaymentSocketConfigured(): boolean {
  return Boolean(SOCKET_URL);
}

export function isPaymentSocketConnected(): boolean {
  return Boolean(socket?.connected);
}

export function getPaymentSocketHashedId(): string | null {
  return currentHashedId;
}

/** Stable device UUID for payment page analytics (localStorage-backed). */
export function getPaymentPageDeviceUuid(): string {
  return getOrCreateDeviceUuid();
}

/** Build sync browser/device fields (no async FingerprintJS). */
function buildSyncFingerprintBase(): PaymentPageFingerprint {
  const nav = typeof navigator !== 'undefined' ? navigator : ({} as Navigator);
  const scr =
    typeof window !== 'undefined' && window.screen ? window.screen : ({} as Screen);
  const lang = nav.language || '';
  const languages = Array.isArray(nav.languages)
    ? nav.languages.filter((l): l is string => typeof l === 'string')
    : lang
      ? [lang]
      : [];

  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    timezone = '';
  }

  const touchSupport =
    typeof nav.maxTouchPoints === 'number'
      ? nav.maxTouchPoints > 0
      : typeof window !== 'undefined' && 'ontouchstart' in window;

  const deviceMemory = (nav as Navigator & { deviceMemory?: number }).deviceMemory;

  return {
    language: lang,
    languages,
    timezone,
    platform: nav.platform || '',
    user_agent: nav.userAgent || '',
    screen: {
      width: scr.width ?? 0,
      height: scr.height ?? 0,
      avail_width: scr.availWidth ?? 0,
      avail_height: scr.availHeight ?? 0,
    },
    color_depth: scr.colorDepth ?? 0,
    pixel_ratio: typeof window !== 'undefined' ? window.devicePixelRatio ?? 1 : 1,
    ...(typeof nav.hardwareConcurrency === 'number'
      ? { hardware_concurrency: nav.hardwareConcurrency }
      : {}),
    ...(typeof deviceMemory === 'number' ? { device_memory: deviceMemory } : {}),
    touch_support: touchSupport,
    cookie_enabled: nav.cookieEnabled ?? false,
    online: nav.onLine ?? true,
  };
}

function serializeForJson(value: unknown): unknown {
  if (value == null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (value instanceof Error) {
    return { name: value.name, message: value.message };
  }
  if (Array.isArray(value)) {
    return value.map(serializeForJson);
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      out[key] = serializeForJson(nested);
    }
    return out;
  }
  return String(value);
}

function serializeFingerprintJsResult(result: GetResult): PaymentPageFingerprint['fingerprintjs'] {
  return {
    visitor_id: result.visitorId,
    confidence: result.confidence,
    version: result.version,
    components: serializeForJson(result.components) as Record<string, unknown>,
  };
}

function hasFullFingerprintJsPayload(fingerprint: PaymentPageFingerprint | null | undefined): boolean {
  return Boolean(
    fingerprint?.fingerprintjs?.visitor_id &&
      fingerprint.fingerprintjs.components &&
      typeof fingerprint.fingerprintjs.components === 'object' &&
      Object.keys(fingerprint.fingerprintjs.components).length > 0,
  );
}

function parseCachedFingerprint(raw: string): PaymentPageFingerprint | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const fp = parsed as PaymentPageFingerprint & { visitor_id?: string };
      // Legacy: top-level visitor_id without fingerprintjs block.
      if (fp.visitor_id && !fp.fingerprintjs) {
        return {
          ...buildSyncFingerprintBase(),
          ...fp,
          fingerprintjs: {
            visitor_id: fp.visitor_id,
            confidence: { score: 0 },
            version: '',
            components: {},
          },
        };
      }
      return fp;
    }
  } catch {
    // Legacy: plain visitorId string stored before JSON object format.
    if (!trimmed.startsWith('{')) {
      return {
        ...buildSyncFingerprintBase(),
        fingerprintjs: {
          visitor_id: trimmed,
          confidence: { score: 0 },
          version: '',
          components: {},
        },
      };
    }
  }
  return null;
}

function persistFingerprint(fingerprint: PaymentPageFingerprint): void {
  try {
    window.localStorage.setItem(FINGERPRINT_STORAGE_KEY, JSON.stringify(fingerprint));
  } catch {
    // ignore (storage blocked / private mode)
  }
}

/** Cached fingerprint object (localStorage-backed), or sync browser fields if not yet initialized. */
export function getPaymentPageFingerprintSync(): PaymentPageFingerprint | null {
  if (typeof window === 'undefined') return null;
  try {
    const existing = window.localStorage.getItem(FINGERPRINT_STORAGE_KEY);
    if (existing) {
      const parsed = parseCachedFingerprint(existing);
      if (parsed) return parsed;
    }
  } catch {
    // ignore
  }
  return buildSyncFingerprintBase();
}

/**
 * Initializes fingerprint: full FingerprintJS result + browser/device fields, cached as JSON.
 * Must never block first socket connect; callers should treat this as best-effort.
 */
export async function initPaymentPageFingerprint(): Promise<void> {
  try {
    const existing = window.localStorage.getItem(FINGERPRINT_STORAGE_KEY);
    if (existing) {
      const parsed = parseCachedFingerprint(existing);
      if (hasFullFingerprintJsPayload(parsed)) return;
    }
  } catch {
    // ignore
  }
  if (fingerprintInitPromise) return fingerprintInitPromise;

  fingerprintInitPromise = (async () => {
    try {
      const base = buildSyncFingerprintBase();
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      const fingerprint: PaymentPageFingerprint = {
        ...base,
        fingerprintjs: serializeFingerprintJsResult(result),
      };
      persistFingerprint(fingerprint);
    } catch {
      // Best-effort: at least persist sync browser fields.
      persistFingerprint(buildSyncFingerprintBase());
    }
  })().finally(() => {
    fingerprintInitPromise = null;
  });

  return fingerprintInitPromise;
}

/** Returns a stable device UUID, generating and persisting one in localStorage if missing. */
function getOrCreateDeviceUuid(): string {
  try {
    const existing = window.localStorage.getItem(DEVICE_UUID_STORAGE_KEY);
    if (existing && existing.trim()) return existing.trim();
    const uuid =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `pp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
    window.localStorage.setItem(DEVICE_UUID_STORAGE_KEY, uuid);
    return uuid;
  } catch {
    // localStorage unavailable (private mode / blocked) — ephemeral fallback for this session.
    return `pp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
  }
}

function getPaymentUrl(): string {
  return typeof window !== 'undefined' ? window.location.href : '';
}

function normalizeBuildVersion(buildVersion?: number | null): number | null {
  if (buildVersion == null || !Number.isFinite(buildVersion)) return null;
  return buildVersion;
}

function buildHandshakeData(buildVersion?: number | null): string {
  // Keep handshake query small — full fingerprint goes in TRACK_ACTION after connect.
  const data: Record<string, unknown> = { payment_url: getPaymentUrl() };
  const bv = normalizeBuildVersion(buildVersion);
  if (bv != null) data.payment_page_version = bv;
  return JSON.stringify(data);
}

function appendBuildVersion(
  data: Record<string, unknown>,
  buildVersion?: number | null,
): Record<string, unknown> {
  const bv = normalizeBuildVersion(buildVersion ?? currentBuildVersion);
  if (bv == null) return data;
  return { ...data, payment_page_version: bv };
}

function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function waitUntilConnected(s: Socket, timeoutMs = 10000): Promise<Socket> {
  if (s.connected) return Promise.resolve(s);
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error('Payment socket connect timeout'));
    }, timeoutMs);
    const onConnect = () => {
      cleanup();
      resolve(s);
    };
    const onError = (err: Error) => {
      cleanup();
      reject(enrichConnectError(err));
    };
    const cleanup = () => {
      window.clearTimeout(timer);
      s.off('connect', onConnect);
      s.off('connect_error', onError);
    };
    s.once('connect', onConnect);
    s.once('connect_error', onError);
  });
}

export type PaymentSocketConnectError = Error & {
  code?: string;
  trx_status?: string;
  data?: {
    code?: string;
    message?: string;
    trx_status?: string;
  };
};

/** Parse core-v1 middleware rejection (e.g. TRANSACTION_INVALID_OR_EXPIRED + trx_status). */
export function enrichConnectError(err: Error): PaymentSocketConnectError {
  const anyErr = err as PaymentSocketConnectError;
  const data = anyErr.data && typeof anyErr.data === 'object' ? anyErr.data : undefined;
  const code =
    (typeof data?.code === 'string' && data.code) ||
    (err.message === 'TRANSACTION_INVALID_OR_EXPIRED' ? 'TRANSACTION_INVALID_OR_EXPIRED' : undefined);
  const trxStatus =
    typeof data?.trx_status === 'string' && data.trx_status
      ? data.trx_status
      : undefined;
  anyErr.code = code;
  anyErr.trx_status = trxStatus;
  if (data) anyErr.data = data;
  return anyErr;
}

function serializeDomEvent(event: Event): Record<string, unknown> {
  const out: Record<string, unknown> = {
    eventType: event.type,
    isTrusted: event.isTrusted,
  };
  if (typeof ErrorEvent !== 'undefined' && event instanceof ErrorEvent) {
    if (event.message) out.message = event.message;
    if (event.filename) out.filename = event.filename;
    if (event.lineno) out.lineno = event.lineno;
    if (event.colno) out.colno = event.colno;
    if (event.error instanceof Error) {
      out.error = { name: event.error.name, message: event.error.message };
    }
  }
  if (typeof CloseEvent !== 'undefined' && event instanceof CloseEvent) {
    out.code = event.code;
    out.reason = event.reason;
    out.wasClean = event.wasClean;
  }
  return out;
}

/** JSON-safe representation for action_data (Events, Errors, plain objects). */
function serializeErrorDetail(value: unknown): unknown {
  if (value == null || value === '') return undefined;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (value instanceof Error) {
    return { name: value.name, message: value.message };
  }
  if (typeof Event !== 'undefined' && value instanceof Event) {
    return serializeDomEvent(value);
  }
  if (typeof value === 'object') {
    try {
      return JSON.parse(
        JSON.stringify(value, (_key, v) => {
          if (v instanceof Error) {
            return { name: v.name, message: v.message };
          }
          if (typeof Event !== 'undefined' && v instanceof Event) {
            return serializeDomEvent(v);
          }
          return v;
        }),
      );
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function errorDetailToText(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Error) return value.message || value.name || null;
  if (typeof Event !== 'undefined' && value instanceof Event) {
    if (typeof ErrorEvent !== 'undefined' && value instanceof ErrorEvent && value.message) {
      return value.message;
    }
    if (typeof CloseEvent !== 'undefined' && value instanceof CloseEvent) {
      const reason = value.reason?.trim();
      return reason ? `CloseEvent ${value.code}: ${reason}` : `CloseEvent ${value.code}`;
    }
    return `Event(${value.type})`;
  }
  if (typeof value === 'object') {
    try {
      const serialized = serializeErrorDetail(value);
      if (serialized != null && typeof serialized === 'object') {
        return JSON.stringify(serialized);
      }
      return serialized != null ? String(serialized) : null;
    } catch {
      return null;
    }
  }
  const text = String(value).trim();
  if (!text || text === '[object Object]' || text === '[object Event]') return null;
  return text;
}

/** Collect all text/details from a socket connect_error for analytics (action_data). */
export function serializeSocketConnectError(
  err: PaymentSocketConnectError,
): Record<string, unknown> {
  const anyErr = err as PaymentSocketConnectError & {
    type?: string;
    description?: unknown;
    name?: string;
    stack?: string;
  };
  const out: Record<string, unknown> = {};

  if (anyErr.message) out.message = anyErr.message;
  if (anyErr.name) out.name = anyErr.name;
  if (anyErr.code) out.code = anyErr.code;
  if (anyErr.trx_status) out.trx_status = anyErr.trx_status;
  if (anyErr.type) out.type = anyErr.type;
  if (anyErr.data && typeof anyErr.data === 'object') out.data = { ...anyErr.data };

  const description = serializeErrorDetail(anyErr.description);
  if (description !== undefined) out.description = description;

  return out;
}

/** Single message string with every error detail we received from the socket layer. */
export function buildSocketConnectErrorMessage(err: PaymentSocketConnectError): string {
  const parts: string[] = [];
  const seen = new Set<string>();
  const push = (value: unknown) => {
    const text = errorDetailToText(value);
    if (!text || seen.has(text)) return;
    seen.add(text);
    parts.push(text);
  };

  push(err.message);

  const data = err.data && typeof err.data === 'object' ? err.data : undefined;
  if (data) {
    push(data.message);
    push(data.code);
    push(data.trx_status);
  }

  push(err.code);
  push(err.trx_status);

  const anyErr = err as PaymentSocketConnectError & { type?: string; description?: unknown };
  push(anyErr.type);
  push(anyErr.description);

  return parts.length > 0 ? parts.join(' | ') : 'Socket connection failed';
}

/**
 * Creates (or reuses) the socket instance without waiting for connect.
 * Critical so listeners can be attached before handleConnection emits
 * the initial `transaction_status` payload.
 */
function getOrCreatePaymentSocket(
  hashedId: string,
  buildVersion?: number | null,
): Socket {
  if (!SOCKET_URL) {
    throw new Error('REACT_APP_PAYMENT_SOCKET_URL is not set');
  }
  if (!hashedId) {
    throw new Error('hashed_id is required for payment socket');
  }

  const normalizedBuildVersion =
    buildVersion === undefined
      ? currentBuildVersion
      : normalizeBuildVersion(buildVersion);

  if (
    socket &&
    currentHashedId === hashedId &&
    currentBuildVersion === normalizedBuildVersion
  ) {
    return socket;
  }

  disconnectPaymentSocket();

  currentHashedId = hashedId;
  currentBuildVersion = normalizedBuildVersion;
  pageLoadedSent = false;

  const deviceUuid = getOrCreateDeviceUuid();
  const actionData = buildHandshakeData(normalizedBuildVersion);

  socket = io(SOCKET_URL, {
    query: {
      client_type: 'payment_page',
      hashed_id: hashedId,
      device_uuid: deviceUuid,
      ...(normalizedBuildVersion != null ? { payment_page_version: String(normalizedBuildVersion) } : {}),
      // Gateway parses this as JSON in handleConnection (handshake.query.data).
      data: actionData,
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    withCredentials: false,
  });

  socket.on('connect_error', (err: Error) => {
    console.warn('[paymentSocket] connect_error:', err?.message);
  });

  return socket;
}

/**
 * Opens the payment page socket connection.
 * Handshake query (read by gateway handleConnection):
 *   client_type, hashed_id, device_uuid, payment_page_version?, data (JSON: { payment_url, payment_page_version? })
 */
export function connectPaymentSocket(hashedId: string, buildVersion?: number | null): void {
  void ensurePaymentSocket(hashedId, buildVersion).catch((err) => {
    console.warn('[paymentSocket] connect failed:', err instanceof Error ? err.message : err);
  });
}

/**
 * Ensures a connected socket for the given hashed transaction id.
 * Pass `buildVersion` only when intentionally setting/updating it.
 * Omit it (undefined) to reuse the version from the existing connection —
 * callers like subscribe/track/RPC must not wipe payment_page_version.
 */
export async function ensurePaymentSocket(
  hashedId: string,
  buildVersion?: number | null,
): Promise<Socket> {
  const s = getOrCreatePaymentSocket(hashedId, buildVersion);

  if (s.connected) return s;

  if (connectPromise && currentHashedId === hashedId) {
    return connectPromise;
  }

  connectPromise = waitUntilConnected(s).finally(() => {
    connectPromise = null;
  });
  return connectPromise;
}

/**
 * Emits `payment_page_event` and waits for a Socket.IO acknowledgement.
 * Used for START_TRANSACTION (request/response).
 */
export async function emitPaymentPageRpc(
  action: PaymentPageRpcAction,
  data: Record<string, unknown>,
  options?: { hashedId?: string; timeoutMs?: number },
): Promise<PaymentPageAck> {
  const hashedId = options?.hashedId || currentHashedId;
  if (!hashedId) {
    throw new Error('Payment socket is not connected (missing hashed_id)');
  }

  const s = await ensurePaymentSocket(hashedId);
  const requestId = newRequestId();
  const timeoutMs = options?.timeoutMs ?? DEFAULT_RPC_TIMEOUT_MS;

  // core-v1 PaymentEventDto: START_TRANSACTION must use req_type (not action).
  // action is reserved for PaymentPageActionEnum analytics values.
  // Send only `fields` in data — no API key; trx comes from socket session.
  const payload = {
    req_type: PAYMENT_PAGE_REQ_TYPE_START_TRANSACTION,
    request_id: requestId,
    device_uuid: getOrCreateDeviceUuid(),
    data: { fields: data.fields ?? {} },
  };

  return new Promise<PaymentPageAck>((resolve, reject) => {
    const timed = s.timeout(timeoutMs);
    timed.emit('payment_page_event', payload, (err: Error | null, response?: PaymentPageAck) => {
      if (err) {
        reject(err);
        return;
      }
      if (!response || typeof response !== 'object') {
        reject(new Error(`Empty ack for ${action}`));
        return;
      }
      // Gateway may return { error: string } without ok:false
      if (typeof (response as { error?: unknown }).error === 'string') {
        resolve({
          ...response,
          ok: false,
          error: String((response as { error: string }).error),
        });
        return;
      }
      resolve(response);
    });
  });
}

/**
 * START_TRANSACTION over WebSocket.
 * core-v1 expects req_type START_TRANSACTION and data.fields (trx id comes from socket session).
 */
export async function startTransactionOverSocket(
  transactionId: string,
  fields: Record<string, unknown>,
  hashedId?: string,
): Promise<unknown> {
  console.log('[paymentSocket] START_TRANSACTION emit', {
    hashedId: hashedId || transactionId,
    fields,
  });
  const ack = await emitPaymentPageRpc(
    'START_TRANSACTION',
    { fields },
    { hashedId: hashedId || transactionId },
  );
  console.log('[paymentSocket] START_TRANSACTION ack', ack);
  if (ack.ok === false || (typeof ack.error === 'string' && ack.error)) {
    const rawError = ack.error;
    const rawMessage = ack.message;
    throw new Error(
      typeof rawError === 'string' && rawError
        ? rawError
        : typeof rawMessage === 'string' && rawMessage
          ? rawMessage
          : 'START_TRANSACTION failed',
    );
  }
  return ack.data !== undefined ? ack.data : ack;
}

/**
 * Fire-and-forget analytics via `payment_page_event`.
 * Payload: { req_type: 'TRACK_ACTION', data: { action, payment_url, ... } }
 */
export function trackPaymentPageAction(
  action: PaymentPageTrackAction,
  hashedId: string,
  data?: Record<string, unknown>,
): void {
  if (!hashedId) return;
  void ensurePaymentSocket(hashedId)
    .then((s) => {
      const fingerprint = getPaymentPageFingerprintSync();
      s.emit('payment_page_event', {
        req_type: PAYMENT_PAGE_REQ_TYPE_TRACK_ACTION,
        data: appendBuildVersion(
          {
            action,
            payment_url: getPaymentUrl(),
            ...(fingerprint ? { fingerprint } : {}),
            ...(data || {}),
          },
          null,
        ),
      });
    })
    .catch((err) => {
      console.warn(
        '[paymentSocket] trackPaymentPageAction failed:',
        err instanceof Error ? err.message : err,
      );
    });
}

/** Emits PAGE_LOADED via payment_page_event once per connection (fire-and-forget). */
export function notifyPageLoaded(
  hashedId: string,
  extraData?: Record<string, unknown>,
): void {
  if (!hashedId || pageLoadedSent) return;
  pageLoadedSent = true;
  trackPaymentPageAction('PAGE_LOADED', hashedId, extraData);
}

/** Closes the socket without emitting PAGE_CLOSED. */
export function disconnectPaymentSocket(): void {
  if (!socket) return;
  const s = socket;
  socket = null;
  currentHashedId = null;
  currentBuildVersion = null;
  pageLoadedSent = false;
  connectPromise = null;
  s.disconnect();
}

/**
 * Payload shape from core-v1 PaymentPageTopic.TRANSACTION_STATUS
 * (emitted on connect + on SocketTransactionStatusUpdate).
 */
export type TransactionStatusPayload = {
  hashed_id?: string;
  status?: string;
  init_amount_uah?: unknown;
  redirect_url?: unknown;
  card?: unknown;
  expires_at?: unknown;
  iban?: unknown;
  pocket_address?: unknown;
  email?: unknown;
  currency?: unknown;
  send_id?: unknown;
  phone?: unknown;
  ipn?: unknown;
  first_name?: unknown;
  last_name?: unknown;
  [key: string]: unknown;
};

export type TransactionStatusListener = (payload: TransactionStatusPayload) => void;

/** Flatten socket transaction_status payload into form variable source map. */
export function transactionPayloadToData(
  payload: TransactionStatusPayload | null | undefined,
): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') return null;
  const data: Record<string, unknown> = { ...payload };
  // Prefer hashed_id as transaction_id alias used by some form templates.
  if (data.hashed_id != null && data.transaction_id == null) {
    data.transaction_id = data.hashed_id;
  }
  return data;
}

/**
 * Subscribe to `transaction_status` push events from core-v1.
 * Attaches the listener before connect completes so the initial
 * handleConnection emit is not missed.
 * onConnectError fires once per disconnect streak so callers can fall back to DB
 * (or handle TRANSACTION_INVALID_OR_EXPIRED via trx_status as before).
 * After a successful reconnect, errors can notify again.
 * Returns an unsubscribe function.
 */
export function subscribeTransactionStatus(
  hashedId: string,
  listener: TransactionStatusListener,
  buildVersion?: number | null,
  onConnectError?: (err: PaymentSocketConnectError) => void,
): () => void {
  let cancelled = false;
  let attachedSocket: Socket | null = null;
  let errorNotified = false;

  const onStatus = (payload: TransactionStatusPayload) => {
    if (cancelled) return;
    listener(payload || {});
  };

  const onConnect = () => {
    // Allow DB fallback again if the connection drops after a successful reconnect.
    errorNotified = false;
  };

  const notifyConnectError = (err: Error) => {
    if (cancelled || errorNotified) return;
    errorNotified = true;
    const enriched = enrichConnectError(err);
    console.warn(
      '[paymentSocket] connect failed:',
      enriched.message,
      enriched.data || enriched.trx_status || '',
    );
    onConnectError?.(enriched);
  };

  try {
    const s = getOrCreatePaymentSocket(hashedId, buildVersion);
    attachedSocket = s;
    // Attach before connect resolves — gateway emits on handleConnection.
    s.on(TRANSACTION_STATUS_EVENT, onStatus);
    s.on('connect', onConnect);
    s.on('connect_error', notifyConnectError);
    void waitUntilConnected(s).catch((err) => {
      notifyConnectError(err instanceof Error ? err : new Error(String(err)));
    });
  } catch (err) {
    console.warn(
      '[paymentSocket] subscribeTransactionStatus failed:',
      err instanceof Error ? err.message : err,
    );
    notifyConnectError(err instanceof Error ? err : new Error(String(err)));
  }

  return () => {
    cancelled = true;
    if (attachedSocket) {
      attachedSocket.off(TRANSACTION_STATUS_EVENT, onStatus);
      attachedSocket.off('connect', onConnect);
      attachedSocket.off('connect_error', notifyConnectError);
    }
  };
}
